/** @jsxImportSource @emotion/react */
import { createContext, useState, useContext, ReactNode, FC, useCallback } from 'react';
import { ThemeProvider as EmotionThemeProvider, Global } from '@emotion/react';

import {
  lightTheme,
  darkTheme,
  GlobalStyles,
  type ThemeMode, // union of the two palettes
} from '../theme';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
export interface ThemeContextType {
  theme: ThemeMode;
  isLight: boolean;
  toggleTheme: () => void;
}

/* ------------------------------------------------------------------ */
/* Context + hook                                                      */
/* ------------------------------------------------------------------ */
const ThemeContext = createContext<ThemeContextType>({
  theme: lightTheme,
  isLight: true,
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeMode>(lightTheme);

  const isLight = theme === lightTheme;

  const toggleTheme = useCallback(
    () => setTheme((prev) => (prev === lightTheme ? darkTheme : lightTheme)),
    []
  );

  return (
    <ThemeContext.Provider value={{ theme, isLight, toggleTheme }}>
      <EmotionThemeProvider theme={theme}>
        <Global styles={GlobalStyles(theme)} />
        {children}
      </EmotionThemeProvider>
    </ThemeContext.Provider>
  );
};
